// Free `/audit` -- matching the audited PAGE to a Google Business Profile
// without the owner's help (reconciliation item 5, decision 3-A, 2026-09-03).
// Pure leaf, no imports: the visitor typed a URL, so everything we know
// about the business comes from that page's HTML. A wrong match would show
// a stranger's profile as "yours", so the rule is CONFIDENT-OR-NOTHING: the
// candidate Google returns must share the page's host or its phone number,
// or the profile legs are omitted and the report says why.

export interface PageBusinessHints {
  /** Best guess at the business name: JSON-LD LocalBusiness name, else
   *  og:site_name, else the first segment of <title>. */
  name: string | null;
  /** First NANP phone on the page, digits only (10). */
  phoneDigits: string | null;
  /** First US-shaped street address on the page. */
  address: string | null;
  /** The page's SITE KEY: host lowercased, leading "www." removed -- plus
   *  the first path segment on a multi-tenant host (web.pageboss.com/{slug}),
   *  where the host alone names every customer at once. */
  host: string;
}

export interface PlaceCandidate {
  /** The profile's website URL as Google returned it (null when absent). */
  websiteUrl: string | null;
  phoneDigits: string | null;
  /** The profile's formatted address as Google returned it (null when absent). */
  address: string | null;
}

/** Street number + ZIP of a US-shaped address, or null. The two tokens that
 *  distinguish one branch of a brand from another. */
export function addressAnchors(address: string): { number: string; zip: string } | null {
  const number = /^\s*(\d{1,6})\b/.exec(address)?.[1];
  const zip = /\b(\d{5})(?:-\d{4})?\s*$/.exec(address.trim())?.[1] ?? /\b[A-Z]{2}\s*(\d{5})\b/.exec(address)?.[1];
  return number && zip ? { number, zip } : null;
}

/** True when the candidate's address carries the page address's street
 *  number AND ZIP -- the cheap check that a same-brand profile is THIS
 *  location and not another branch. */
export function sameLocation(pageAddress: string, candidateAddress: string | null): boolean {
  if (!candidateAddress) return false;
  const a = addressAnchors(pageAddress);
  if (!a) return false;
  const hasNumber = new RegExp(`(^|\\D)${a.number}(\\D|$)`).test(candidateAddress);
  const hasZip = candidateAddress.includes(a.zip);
  return hasNumber && hasZip;
}

/** Hosts where the FIRST path segment identifies the business, not the host. */
const MULTI_TENANT_HOSTS: ReadonlySet<string> = new Set(['web.pageboss.com']);

/** host (www-stripped) -- or host/segment on a multi-tenant host. '' when the
 *  URL does not parse. Used for cache keys and for host matching so two
 *  pageboss-hosted customers can never share a cached profile or match each
 *  other's website link. */
export function siteKeyFromUrl(url: string): string {
  try {
    const u = new URL(url);
    const host = normalizeHost(u.hostname);
    if (!MULTI_TENANT_HOSTS.has(host)) return host;
    const first = u.pathname.split('/').filter(Boolean)[0]?.toLowerCase() ?? '';
    return first ? `${host}/${first}` : host;
  } catch {
    return '';
  }
}

export type PlaceMatch = 'confident' | 'none';

const PHONE = /(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]\d{3}[-.\s]\d{4}\b/;
// House number must not be the tail of a phone number ("...-0142 220 Main
// St" after entity stripping), hence the digit/dash lookbehind.
// The street/city separator may be a comma OR just whitespace (a <br> in the
// footer becomes a space once tags are stripped).
const ADDRESS = /(?<![\d-])\b\d{1,6}\s+[^,\n<]{3,60}?(?:,|\s)\s*[^,\n<]{2,40}?,?\s*[A-Z]{2}\s+\d{5}(?:-\d{4})?\b/;

export function normalizePhoneDigits(raw: string): string | null {
  let d = raw.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  return d.length === 10 ? d : null;
}

export function normalizeHost(hostname: string): string {
  return hostname.toLowerCase().replace(/^www\./, '');
}

function decodeEntities(s: string): string {
  return s
    .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec: string) => String.fromCodePoint(Number(dec)))
    .replace(/&amp;/g, '&')
    .replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&(nbsp|ndash|mdash|middot|bull|hellip|rsquo|lsquo|rdquo|ldquo);/g, (_, n: string) =>
      ({ nbsp: ' ', ndash: '\u2013', mdash: '\u2014', middot: '\u00b7', bull: '\u2022', hellip: '\u2026', rsquo: '\u2019', lsquo: '\u2018', rdquo: '\u201d', ldquo: '\u201c' })[n] ?? ' ',
    );
}

/** Values site builders emit when the field is unset. */
const PLACEHOLDER_VALUES = new Set(['', 'null', 'undefined', 'none', 'n/a', 'home', 'homepage', 'welcome', 'untitled']);

function cleanName(raw: string | null): string | null {
  if (raw === null) return null;
  const v = decodeEntities(raw).replace(/\s+/g, ' ').trim();
  return PLACEHOLDER_VALUES.has(v.toLowerCase()) ? null : v.slice(0, 120);
}

/** Letters+digits only, lowercased -- for comparing a title segment with the
 *  domain label ("Phil's BBQ" -> "philsbbq" vs philsbbq.com). */
function squash(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Picks the <title> segment most likely to be the business name: the one
 *  that matches the domain label (either contains the other, >= 5 chars),
 *  else the first non-generic segment ("Name - City" is the common shape).
 *  Generic segments ("Home", "Welcome") are never candidates. */
export function pickNameSegment(title: string, host: string): string | null {
  const segments = title
    .split(/\s+[|\-\u2013\u2014:\u00b7\u2022]\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length >= 2 && !PLACEHOLDER_VALUES.has(s.toLowerCase()));
  if (segments.length === 0) return null;
  const label = squash(host.split('/')[0]?.split('.')[0] ?? '');
  if (label.length >= 5) {
    const byDomain = segments.find((s) => {
      const q = squash(s);
      return q.length >= 5 && (q.includes(label) || label.includes(q));
    });
    if (byDomain) return byDomain;
  }
  return segments[0]!;
}

const NOT_A_BUSINESS = new Set([
  'WebSite', 'WebPage', 'CollectionPage', 'AboutPage', 'ContactPage', 'BreadcrumbList', 'ListItem', 'ItemList',
  'ImageObject', 'VideoObject', 'Article', 'BlogPosting', 'NewsArticle', 'Person', 'FAQPage', 'Question', 'Answer',
  'Offer', 'Product', 'Event', 'Review', 'Rating', 'AggregateRating', 'SearchAction', 'PostalAddress', 'GeoCoordinates',
  'OpeningHoursSpecification', 'Service', 'HowTo', 'HowToStep',
  // navigation/page-chrome nodes (WordPress/Yoast/Rank Math emit these on every page): a nav item named
  // "Home" is not the business — it sent the free check to Google asking for "Home" (2026-09-04).
  'SiteNavigationElement', 'WebPageElement', 'WPHeader', 'WPFooter', 'WPSideBar', 'Menu', 'MenuItem',
  'ReadAction', 'EntryPoint', 'Thing', 'CreativeWork', 'MediaObject', 'ContactPoint',
]);

function jsonLdName(html: string): string | null {
  const blocks = html.match(/<script[^>]+type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi) ?? [];
  for (const block of blocks) {
    const body = block.replace(/^<script[^>]*>/i, '').replace(/<\/script>$/i, '');
    let parsed: unknown;
    try {
      parsed = JSON.parse(body);
    } catch {
      continue;
    }
    const nodes: unknown[] = [];
    const push = (n: unknown) => {
      if (Array.isArray(n)) n.forEach(push);
      else if (n && typeof n === 'object') {
        nodes.push(n);
        const g = (n as Record<string, unknown>)['@graph'];
        if (Array.isArray(g)) g.forEach(push);
      }
    };
    push(parsed);
    for (const n of nodes) {
      const o = n as Record<string, unknown>;
      const t = o['@type'];
      const types = Array.isArray(t) ? t.map(String) : t ? [String(t)] : [];
      // Any typed node that is not a page/content/person artefact and has a
      // name is the business (LocalBusiness subtypes -- Plumber, Dentist,
      // HairSalon -- rarely contain the word "Business").
      const isBusinessLike = types.length > 0 && !types.some((x) => NOT_A_BUSINESS.has(x));
      if (isBusinessLike && typeof o['name'] === 'string') {
        const n = cleanName(o['name']);
        if (n) return n;
      }
    }
  }
  return null;
}

function metaContent(html: string, prop: string): string | null {
  const re = new RegExp(`<meta[^>]+(?:property|name)=["']${prop}["'][^>]*content=["']([^"']*)["']`, 'i');
  const m = re.exec(html);
  if (m) return cleanName(m[1] ?? '');
  const re2 = new RegExp(`<meta[^>]+content=["']([^"']*)["'][^>]*(?:property|name)=["']${prop}["']`, 'i');
  const m2 = re2.exec(html);
  return m2 ? cleanName(m2[1] ?? '') : null;
}

function titleName(html: string, host: string): string | null {
  const m = /<title>([\s\S]*?)<\/title>/i.exec(html);
  if (!m) return null;
  const raw = decodeEntities(m[1]!.replace(/\s+/g, ' ').trim());
  return cleanName(pickNameSegment(raw, host));
}

/** Everything the page tells us about the business it belongs to. */
export function extractBusinessHints(html: string, pageUrl: string): PageBusinessHints {
  const host = siteKeyFromUrl(pageUrl);
  const text = html.replace(/<script[\s\S]*?<\/script>|<style[\s\S]*?<\/style>/gi, ' ');
  // A tel: link is the most reliable phone on a real site (the visible text
  // is often an image or a formatted span); fall back to the first NANP
  // pattern in the text.
  const tel = /href=["']tel:([^"']+)["']/i.exec(html);
  const telDigits = tel ? normalizePhoneDigits(decodeEntities(tel[1]!)) : null;
  const phone = PHONE.exec(text);
  // Tags and entities become separators so "&middot;" or "<br>" between the
  // phone and the address never glue the two together.
  const plain = text.replace(/<[^>]+>/g, ' ').replace(/&[a-z#0-9]+;/gi, ' ');
  const address = ADDRESS.exec(phone ? plain.replace(phone[0], ' ') : plain);
  return {
    name: jsonLdName(html) ?? metaContent(html, 'og:site_name') ?? titleName(html, host),
    phoneDigits: telDigits ?? (phone ? normalizePhoneDigits(phone[0]) : null),
    address: address ? address[0].replace(/\s+/g, ' ').trim() : null,
    host,
  };
}

/** The text query for Places searchText, or null when the page gave us no
 *  name to search for (an address alone finds the building, not the business). */
export function buildPlaceQuery(hints: PageBusinessHints): string | null {
  if (!hints.name) return null;
  return hints.address ? `${hints.name} ${hints.address}` : hints.name;
}

/** Confident only when the returned profile shares the page's phone, or
 *  shares its website AND its street number + ZIP (the page must carry an
 *  address). A host match alone is never enough: a chain links every
 *  location to the same corporate site, so the first text-search hit could
 *  be any branch -- someone else's reviews scored as "yours". */
export function judgePlaceMatch(hints: PageBusinessHints, candidate: PlaceCandidate): PlaceMatch {
  if (candidate.phoneDigits && hints.phoneDigits && candidate.phoneDigits === hints.phoneDigits) return 'confident';
  const candidateKey = candidate.websiteUrl ? siteKeyFromUrl(candidate.websiteUrl) : '';
  if (hints.address && candidateKey && hints.host && candidateKey === hints.host && sameLocation(hints.address, candidate.address)) {
    return 'confident';
  }
  return 'none';
}

// --- Candidate loop (2026-09-04) ---------------------------------------------
// Google's text search is asked for up to five hits (packages/places
// AUDIT_MAX_CANDIDATES). The first hit is its best GUESS, not the answer: a
// common name puts a namesake first and the real profile second, and judging
// only the first meant "no match" for a business Google had in fact found.

/** A text-search hit as the free check reads it: the match fields plus what
 *  the "profiles linked to this site" list shows. */
export interface RankedPlaceCandidate extends PlaceCandidate {
  placeId: string;
  name: string;
  rating: number | null;
  ratingCount: number | null;
}

/** One Google profile that links to the audited site -- LISTED, never
 *  scored (a multi-location brand is not one business). */
export interface LinkedProfile {
  name: string;
  address: string | null;
  rating: number | null;
  votes: number | null;
  placeId: string;
}

/** Judges `candidates` in Google's order; the FIRST confident one wins, so a
 *  namesake ahead of the real profile no longer hides it. Null when none. */
export function pickConfidentCandidate<T extends PlaceCandidate>(
  hints: PageBusinessHints,
  candidates: readonly T[],
): T | null {
  const confident = candidates.filter((c) => judgePlaceMatch(hints, c) === 'confident');
  if (confident.length === 0) return null;
  // A brand's shared central number makes every branch "confident" on the
  // phone rule; when the page carries an address, the branch AT that address
  // outranks an earlier sibling (review finding C1, 2026-09-04).
  if (hints.address) {
    const atAddress = confident.find((c) => sameLocation(hints.address as string, c.address));
    if (atAddress) return atAddress;
  }
  return confident[0] ?? null;
}

/** The candidates whose website resolves to the page's own site key, most-
 *  reviewed first (no count sorts last; ties keep Google's order). Empty
 *  when the page has no host -- '' must never "match" a profile without a
 *  website. Only meaningful when NO candidate was confident: with a match the
 *  legs are scored and this list is not shown. */
export function linkedProfilesForSite(
  hints: Pick<PageBusinessHints, 'host'>,
  candidates: readonly RankedPlaceCandidate[],
): LinkedProfile[] {
  if (!hints.host) return [];
  return candidates
    .filter((c) => c.websiteUrl !== null && siteKeyFromUrl(c.websiteUrl) === hints.host)
    .map((c) => ({ name: c.name, address: c.address, rating: c.rating, votes: c.ratingCount, placeId: c.placeId }))
    .sort((a, b) => (b.votes ?? -1) - (a.votes ?? -1));
}

/** Why the profile legs were omitted, in the visitor's terms. With linked
 *  profiles the reason says the site is multi-location and what the page
 *  lacked; otherwise it names Google's closest hit (the first candidate)
 *  and what it failed to share, exactly as before. */
export function profileOmissionReason(
  hints: PageBusinessHints,
  closest: { name: string },
  linkedCount: number,
): string {
  if (linkedCount >= 1) {
    const n = `${linkedCount} Google profile${linkedCount === 1 ? '' : 's'}`;
    const tag = linkedCount >= 2 ? ' (multi-location)' : '';
    if (!hints.phoneDigits && !hints.address) {
      return `This site links to ${n}${tag} and the page shows no phone or street address, so we did not pick one to score.`;
    }
    const shown = hints.phoneDigits && hints.address ? 'the phone number or street address' : hints.phoneDigits ? 'the phone number' : 'the street address';
    const who = linkedCount === 1 ? 'it does not share' : 'none of them shares';
    return `This site links to ${n}${tag}, but ${who} ${shown} shown on the page, so we did not pick one to score.`;
  }
  const why = hints.address
    ? "does not share this site's phone number, nor both its website link and street address"
    : "does not share this site's phone number, and the page shows no street address to compare";
  return `Google's closest match, "${closest.name}", ${why}, so we did not score it.`;
}
